import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCollectionRequest, requestCollection } from "../api/client";
import type { AdhocRequest } from "../types";

/**
 * Ask for an artifact outside the monitored set to be collected.
 *
 * The page above this has already done the only thing it can do while a
 * reader waits: one live admin call. Anything more costs several calls
 * against an admin API limited to 200 requests an hour, so it is queued and a
 * collector pass picks it up, usually within five minutes.
 *
 * What comes back is deliberately partial, and says so. An ad-hoc request
 * does not make the collector a member of the workspace — that is what
 * monitoring does, and it is a permission change on the tenant, not something
 * to do because a page was opened. So the refresh schedule, the refresh
 * history and the report's pages stay out of reach, and the request lists
 * them rather than letting their absence read as the artifact not having any.
 */

const POLL_MS = 10_000;
const TERMINAL = ["succeeded", "partial", "failed"];

function StatusLine({ request }: { request: AdhocRequest }) {
  if (request.status === "queued") {
    return <>Queued. A collector pass picks this up within a few minutes.</>;
  }
  if (request.status === "running") return <>Collecting now.</>;

  const counts = Object.entries(request.collected ?? {}).filter(([, n]) => n > 0);
  if (!counts.length) {
    return <>{request.note || "Nothing could be collected for this artifact."}</>;
  }
  return (
    <>
      Collected {counts.map(([what, n]) => `${n} ${what}`).join(", ")}. Reload to
      see them.
    </>
  );
}

export default function CollectionRequest({
  workspaceId,
  artifactType,
  artifactId,
  artifactName,
}: {
  workspaceId: string;
  artifactType: string;
  artifactId: string;
  artifactName?: string;
}) {
  const [request, setRequest] = useState<AdhocRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const clearTimer = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = undefined;
  };

  const poll = useCallback(() => {
    fetchCollectionRequest(workspaceId, artifactType, artifactId)
      .then((latest) => {
        setRequest(latest);
        if (latest && !TERMINAL.includes(latest.status)) {
          timer.current = window.setTimeout(poll, POLL_MS);
        }
      })
      .catch(() => {
        // A failed poll says nothing about the request; the next visit will
        // pick it up. Showing an error here would only be noise.
      });
  }, [workspaceId, artifactType, artifactId]);

  useEffect(() => {
    poll();
    return clearTimer;
  }, [poll]);

  const submit = () => {
    setBusy(true);
    setError(null);
    requestCollection(workspaceId, artifactType, artifactId, artifactName)
      .then((created) => {
        setRequest(created);
        clearTimer();
        if (!TERMINAL.includes(created.status)) {
          timer.current = window.setTimeout(poll, POLL_MS);
        }
      })
      .catch((e) => setError(e?.message ?? "Could not queue the request."))
      .finally(() => setBusy(false));
  };

  const inFlight = request?.status === "queued" || request?.status === "running";

  return (
    <div className="rounded-xl border border-info-border bg-info-subtle px-4 py-3 text-sm text-info-fg">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span>
          {request ? (
            <StatusLine request={request} />
          ) : (
            <>
              This workspace is not monitored, so only what the admin API
              returns on its own is shown. A collection pass can retrieve who
              has access and what it reads from.
            </>
          )}
        </span>
        <button
          type="button"
          onClick={submit}
          disabled={busy || inFlight}
          className="shrink-0 rounded-lg border border-info-border bg-surface px-3 py-1.5 text-xs font-semibold text-info-fg disabled:opacity-50"
        >
          {inFlight ? "Requested" : request ? "Request again" : "Collect what is available"}
        </button>
      </div>

      {request?.gaps?.length ? (
        <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs opacity-80">
          {request.gaps.map((gap) => (
            <li key={gap}>{gap}</li>
          ))}
        </ul>
      ) : null}

      {error ? <p className="mt-2 text-xs font-semibold">{error}</p> : null}
    </div>
  );
}
